// ./ui/RecipeDisplay.jsx
import React from 'react';
import { Card, Row, Col, Image, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUtensils, faInfoCircle, faFire, faListAlt,
    faShoePrints, faChartPie, faSeedling // faSeedling para calorías
} from '@fortawesome/free-solid-svg-icons';
import MacronutrientChart from './MacronutrientChart'; // Asume que está en la misma carpeta ui

const IMAGE_API_BASE_POLLINATIONS = 'https://image.pollinations.ai/prompt/';
const DEFAULT_IMAGE_MODEL = 'flux';

const RecipeDisplay = ({ recipeData, imageUrlOverride = null, isNewlyGenerated = false }) => {
    if (!recipeData) return <p>No recipe data to display.</p>;

    const {
        recipeName,
        description,
        prepTime,
        cookTime,
        servings,
        estimatedCaloriesPerServing,
        macrosPer100g,
        cuisineType,
        ingredients,
        steps,
        notes,
        id // Assuming 'id' might be part of recipeData from history
    } = recipeData;

    const imageUrl = imageUrlOverride ||
        `${IMAGE_API_BASE_POLLINATIONS}${encodeURIComponent(
            `A delicious plate of ${recipeName}, ${cuisineType || ''} style, realistic food photography`
        )}?width=512&height=400&model=${DEFAULT_IMAGE_MODEL}&nologo=true&seed=${id || Date.now()}`;

    return (
        <Card className={`mt-${isNewlyGenerated ? '3' : '0'} shadow-lg bg-white border-0 rounded-3 overflow-hidden`}>
            <Row g={0}>
                <Col md={5} className="recipe-image-col p-0 d-flex align-items-stretch">
                    {imageUrl ? (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f0f0f0', minHeight: '300px', maxHeight: '450px' }}>
                            <Image
                                src={imageUrl}
                                alt={`Generated for ${recipeName}`}
                                style={{ width: '100%', height: 'auto', maxHeight: '450px', objectFit: 'contain', borderRadius: isNewlyGenerated ? '0' : '0.375rem 0 0 0.375rem' }}
                                onError={(e) => { e.target.style.display = 'none'; const p = e.target.closest('.recipe-image-col')?.querySelector('.img-placeholder'); if (p) p.style.display = 'flex'; }}
                            />
                            <div className="img-placeholder" style={{ display: 'none', width: '100%', minHeight: '300px', height: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e9ecef', color: '#6c757d', fontSize: '0.9rem' }}>Image could not be loaded.</div>
                        </div>
                    ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center" style={{ minHeight: '300px', backgroundColor: '#e9ecef', color: '#6c757d', fontSize: '0.9rem', borderRadius: isNewlyGenerated ? '0' : '0.375rem 0 0 0.375rem' }}>No image generated.</div>
                    )}
                </Col>
                <Col md={7}>
                    <Card.Body className="p-4 d-flex flex-column h-100">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <h3 className="text-primary mb-0">
                                <FontAwesomeIcon icon={faUtensils} className="me-2" /> {recipeName}
                            </h3>
                            {servings && <Badge bg="info" text="dark" pill className="p-2 fs-6 shadow-sm">{servings} Servings</Badge>}
                        </div>
                        {cuisineType && <div className="small text-muted mb-2">Cuisine: {cuisineType}</div>}
                        {description && <p className="fst-italic text-secondary mb-3 small">{description}</p>}

                        <div className="recipe-meta d-flex justify-content-start flex-wrap gap-3 text-muted small mb-3 p-2 bg-light rounded border">
                            {prepTime && <span><FontAwesomeIcon icon={faInfoCircle} className="me-1 text-primary" />Prep: {prepTime}</span>}
                            {cookTime && <span><FontAwesomeIcon icon={faFire} className="me-1 text-danger" />Cook: {cookTime}</span>}
                            {typeof estimatedCaloriesPerServing === 'number' && <span><FontAwesomeIcon icon={faSeedling} className="me-1 text-success" />Calories per serving: {estimatedCaloriesPerServing} Kcal</span>}
                        </div>

                        {macrosPer100g && (macrosPer100g.carbs > 0 || macrosPer100g.protein > 0 || macrosPer100g.fat > 0) && (
                            <div className="mt-3 pt-3 border-top">
                                <h5 className="text-secondary mb-3 text-center">
                                    <FontAwesomeIcon icon={faChartPie} className="me-2" />
                                    Macronutrient Caloric Ratio (per 100g)
                                </h5>
                                <MacronutrientChart
                                    carbsGrams={macrosPer100g.carbs || 0}
                                    proteinGrams={macrosPer100g.protein || 0}
                                    fatGrams={macrosPer100g.fat || 0}
                                />
                            </div>
                        )}

                        {ingredients && ingredients.length > 0 && (
                            <>
                                <h5 className="text-secondary mb-1 mt-3"><FontAwesomeIcon icon={faListAlt} className="me-2" />Ingredients:</h5>
                                <ListGroup variant="flush" className="ingredients-list small mb-3" style={{ fontSize: '0.85rem' }}>
                                    {ingredients.map((item, i) => (
                                        <ListGroup.Item key={`ing-${i}`} className="px-0 py-1 bg-transparent border-bottom-dashed">{item}</ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </>
                        )}

                        {steps && steps.length > 0 && (
                            <>
                                <h5 className="text-secondary mb-2 mt-2"><FontAwesomeIcon icon={faShoePrints} className="me-2" />Steps:</h5>
                                {steps.map((step, i) => (
                                    <div key={`step-${i}`} className="d-flex mb-2 step-item">
                                        <Badge bg="primary" pill className="me-2 align-self-start mt-1 fs-6 step-number">{i + 1}</Badge>
                                        <p className="mb-0 small lh-base flex-grow-1">{step}</p>
                                    </div>
                                ))}
                            </>
                        )}

                        {notes && notes.length > 0 && (
                            <div className="mt-4 pt-3 border-top">
                                <h5 className="text-secondary mb-3">Chef's Notes:</h5>
                                <ListGroup variant="flush" className="fs-6">
                                    {notes.map((note, i) => (
                                        <ListGroup.Item key={`note-${i}`} className="px-0 py-2 border-0 bg-transparent fst-italic">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info fs-5" />{note}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </div>
                        )}
                    </Card.Body>
                </Col>
            </Row>
        </Card>
    );
};

export default RecipeDisplay;